import React from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import { Card, StatusPill } from '../../components/shared/DataToolbar';
import { Button } from '../../components/ui/button';
import { LoadingState, ErrorState } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import { getBooking, listBookingMessages } from '../../api/hostApi';
import { ArrowLeft, MessageSquare, MapPin, Users, CalendarDays, Receipt, CreditCard, Hash } from 'lucide-react';

const PLATFORM_FEE_PERCENT = 12;
const coverUrl = (booking) => booking?.property?.cover_image?.url || booking?.property?.cover_image || '';
const paymentStatus = (booking) => booking?.transaction?.status || (booking?.paid_at ? 'paid' : booking?.status === 'pending_payment' ? 'pending' : '—');
const money = (value, currency = 'USD') => value == null || value === '' ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(Number(value));
const dateTime = (value) => value ? new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

export default function BookingDetail() {
  const { id } = useParams();
  const { data: booking, loading, error, refetch } = useApi(() => getBooking(id), [id]);
  const { data: msgData } = useApi(() => listBookingMessages(id), [id]);
  const messages = Array.isArray(msgData) ? msgData : (msgData?.items || msgData?.data || []);

  if (loading) return <LoadingState label="Loading booking details…" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!booking) return null;

  const total = Number(booking.total_amount || 0);
  const platformFee = total * (PLATFORM_FEE_PERCENT / 100);
  const payout = total - platformFee;
  const primaryGuest = booking.guest?.name || booking.customer?.name || '—';
  const additionalGuests = booking.guest?.additional_names || [];

  return (
    <div>
      <PageHeader
        breadcrumbs={['Bookings', booking.reference || `#${booking.id}`]}
        title={booking.reference || `Booking #${booking.id}`}
        description={`Created ${dateTime(booking.created_at)}`}
        actions={<Button asChild variant="outline" className="h-10"><Link to="/host/bookings"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Link></Button>}
      />

      <div className="grid gap-6 xl:grid-cols-[1.45fr_0.8fr]">
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill status={booking.status} />
              <StatusPill status={paymentStatus(booking)} />
            </div>

            {booking.property && (
              <div className="mt-5 flex items-center gap-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                {coverUrl(booking) ? <img src={coverUrl(booking)} className="h-20 w-28 rounded-lg object-cover" alt="" /> : <div className="h-20 w-28 rounded-lg bg-slate-100" />}
                <div className="min-w-0">
                  <div className="truncate text-lg font-bold text-slate-900">{booking.property.title}</div>
                  <div className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {booking.property.location || 'Location unavailable'}</div>
                </div>
              </div>
            )}

            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <InfoRow icon={CalendarDays} label="Check-in" value={booking.check_in} />
              <InfoRow icon={CalendarDays} label="Check-out" value={booking.check_out} />
              <InfoRow icon={Users} label="Guests" value={booking.guests} />
              <InfoRow icon={Receipt} label="Nights" value={booking.nights} />
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-900">Guest details</h3>
            <p className="mt-0.5 text-xs text-slate-500">Names and contact details captured during checkout.</p>
            <div className="mt-5 grid gap-5 md:grid-cols-2">
              <Detail label="Primary guest" value={primaryGuest} />
              <Detail label="Email" value={booking.guest?.email || booking.customer?.email || '—'} />
              <Detail label="Phone" value={booking.guest?.phone || booking.customer?.phone || '—'} />
              <Detail label="Additional guests" value={additionalGuests.length ? additionalGuests.join(', ') : 'None listed'} />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900">Payment breakdown</h3>
                <p className="mt-0.5 text-xs text-slate-500">StayEasy destination-charge split for this reservation.</p>
              </div>
              <CreditCard className="h-5 w-5 text-slate-400" />
            </div>
            <div className="mt-5 space-y-3 text-sm">
              <Line label={`Booking total (${booking.nights || 0} nights)`} value={money(total, booking.currency)} />
              <Line label={`StayEasy platform fee (${PLATFORM_FEE_PERCENT}%)`} value={`-${money(platformFee, booking.currency)}`} />
              <div className="flex items-center justify-between border-t border-slate-200 pt-3 font-bold text-slate-900"><span>Estimated host share</span><span className="text-teal-700">{money(payout, booking.currency)}</span></div>
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Guest contact</div>
            <div className="mt-3">
              <div className="font-semibold text-slate-900">{primaryGuest}</div>
              <div className="mt-1 text-xs text-slate-500">{booking.guest?.email || booking.customer?.email || '—'}</div>
              {(booking.guest?.phone || booking.customer?.phone) && <div className="text-xs text-slate-500">{booking.guest?.phone || booking.customer?.phone}</div>}
            </div>
            <Button variant="outline" className="mt-4 w-full" asChild><Link to="/host/messages"><MessageSquare className="mr-1.5 h-4 w-4" /> Open messages ({messages.length})</Link></Button>
          </Card>

          <Card className="p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment references</div>
            <div className="mt-4 space-y-4">
              <Detail label="Payment status" value={paymentStatus(booking)} capitalize />
              <Detail label="Transaction ID" value={booking.transaction?.transaction_id || '—'} mono />
              <Detail label="Invoice number" value={booking.transaction?.invoice_number || '—'} mono />
              <Detail label="Stripe Payment Intent" value={booking.stripe_payment_intent_id || '—'} mono />
              <Detail label="Stripe Checkout Session" value={booking.stripe_checkout_session_id || '—'} mono />
              <Detail label="Paid at" value={dateTime(booking.paid_at || booking.transaction?.paid_at)} />
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Hash className="h-3.5 w-3.5" /> Reservation reference</div>
            <div className="mt-3 font-mono text-sm font-semibold text-slate-900">{booking.reference || booking.id}</div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Icon className="h-4 w-4" /> {label}</div>
      <div className="mt-2 text-lg font-bold text-slate-900">{value ?? '—'}</div>
    </div>
  );
}

function Detail({ label, value, mono, capitalize }) {
  return <div><div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</div><div className={`mt-1 break-all text-sm text-slate-900 ${mono ? 'font-mono text-xs' : 'font-medium'} ${capitalize ? 'capitalize' : ''}`}>{value || '—'}</div></div>;
}

function Line({ label, value }) {
  return <div className="flex items-center justify-between gap-4"><span className="text-slate-500">{label}</span><span className="tabular-nums text-slate-900">{value}</span></div>;
}
